/**
 * Mobile Quick Add Drawer Component
 * Handles bottom slide-up drawer for mobile quick add to cart
 * Uses AJAX + sessionStorage cache for optimal performance
 */

import {createFocusTrap} from 'focus-trap';

class QuickAddDrawer {
  constructor() {
    this.drawer = document.getElementById('quick-add-drawer');
    if (!this.drawer) return;

    this.dialog = this.drawer.querySelector('.quick-add-drawer__dialog');
    this.overlay = this.drawer.querySelector('[data-overlay]');
    this.titleEl = this.drawer.querySelector('.quick-add-drawer__title');
    this.closeBtn = this.drawer.querySelector('.quick-add-drawer__close');
    this.skeleton = this.drawer.querySelector('[data-skeleton]');
    this.content = this.drawer.querySelector('[data-content]');
    this.errorContainer = this.drawer.querySelector('[data-error]');
    this.retryBtn = this.drawer.querySelector('[data-retry]');
    this.colorsContainer = this.drawer.querySelector('[data-colors-container]');
    this.sizesContainer = this.drawer.querySelector('[data-sizes-container]');
    this.sizesFieldset = this.drawer.querySelector('.quick-add-drawer__sizes-fieldset');
    this.addButton = this.drawer.querySelector('[data-add-to-bag-button]');

    this.triggerData = null;
    this.productData = null;
    this.selectedColor = null;
    this.selectedSize = null;
    this.selectedVariantId = null;
    this.isOpen = false;
    this.isLoading = false;

    this.cache = new Map();

    // Viewport detection using matchMedia for accurate responsive behavior
    this.mobileMediaQuery = window.matchMedia('(max-width: 767px)');
    this.isMobile = this.mobileMediaQuery.matches;

    this.focusTrap = createFocusTrap(this.drawer, {
      initialFocus: this.closeBtn,
      allowOutsideClick: true,
      clickOutsideDeactivates: true,
    });

    this.init();
  }

  init() {
    this.closeBtn.addEventListener('click', () => this.close());
    this.overlay.addEventListener('click', () => this.close());
    this.addButton.addEventListener('click', () => this.addToCart());
    if (this.retryBtn) {
      this.retryBtn.addEventListener('click', () => this.retryLoad());
    }

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-quick-add-trigger]');
      if (trigger) {
        event.preventDefault();
        this.handleTriggerClick(trigger);
      }
    });

    document.addEventListener(
      'mouseenter',
      (event) => {
        if (!event.target || typeof event.target.closest !== 'function') {
          return;
        }
        const trigger = event.target.closest('[data-quick-add-trigger]');
        if (trigger && !this.isOpen) {
          this.prefetchProductData(trigger);
        }
      },
      true
    );

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Listen for viewport changes
    this.mobileMediaQuery.addEventListener('change', (event) => {
      this.isMobile = event.matches;
      // Close drawer if user resizes to desktop while drawer is open
      if (!this.isMobile && this.isOpen) {
        this.close();
      }
    });
  }

  /**
   * Handle trigger button click
   */
  async handleTriggerClick(trigger) {
    // Only open drawer on mobile viewports
    if (!this.isMobile) {
      return;
    }

    this.triggerData = {
      handle: trigger.dataset.productHandle,
      title: trigger.dataset.productTitle,
      currentColor: trigger.dataset.currentColor || '',
      stitchedHandles: trigger.dataset.stitchedHandles?.split(',').filter(Boolean) || [],
      stitchingMeta: this.parseJSON(trigger.dataset.stitchingMeta) || [],
    };

    this.openWithLoading();

    try {
      await this.loadProductData();
      this.showContent();
    } catch (error) {
      this.showError(error);
    }
  }

  /**
   * Prefetch product data on hover (performance optimization)
   */
  prefetchProductData(trigger) {
    const handles = trigger.dataset.stitchedHandles?.split(',').filter(Boolean) || [trigger.dataset.productHandle];

    handles.forEach((handle) => {
      if (!this.hasCache(handle)) {
        this.fetchProductFromShopify(handle).catch(() => {
          /* Silently fail prefetch */
        });
      }
    });
  }

  /**
   * Load product data (from cache or AJAX)
   */
  async loadProductData() {
    const {stitchedHandles, stitchingMeta, handle} = this.triggerData;

    const handlesToFetch = stitchedHandles.length > 0 ? stitchedHandles : [handle];

    const promises = handlesToFetch.map((productHandle) => this.getProductData(productHandle));
    const products = await Promise.all(promises);

    this.productData = products.map((product) => {
      const meta = stitchingMeta.find((metaItem) => metaItem.handle === product.handle) || {};
      return {
        handle: product.handle,
        title: product.title,
        color: meta.color || this.extractColorFromTitle(product.title),
        flatlay: meta.flatlay || product.featured_image,
        variants: product.variants,
        available: product.available,
      };
    });

    // Pre-select current color
    // If currentColor is empty but we have a main product handle, find that product's color
    if (!this.triggerData.currentColor && this.triggerData.handle) {
      const currentProduct = this.productData.find((product) => product.handle === this.triggerData.handle);
      this.selectedColor = currentProduct ? currentProduct.color : this.productData[0].color;
    } else {
      this.selectedColor = this.triggerData.currentColor || this.productData[0].color;
    }
  }

  /**
   * Get product data (from cache or fetch)
   */
  async getProductData(handle) {
    if (this.hasCache(handle)) {
      return this.getCache(handle);
    }

    const sessionKey = `quick-add:${handle}`;
    const cachedData = sessionStorage.getItem(sessionKey);
    if (cachedData) {
      try {
        const data = JSON.parse(cachedData);
        this.setCache(handle, data);
        return data;
      } catch (error) {
        /* Invalid cache, fetch fresh */
      }
    }

    const data = await this.fetchProductFromShopify(handle);

    this.setCache(handle, data);
    try {
      sessionStorage.setItem(sessionKey, JSON.stringify(data));
    } catch (error) {
      /* SessionStorage full, continue without caching */
    }

    return data;
  }

  /**
   * Fetch product from Shopify's .js endpoint
   */
  async fetchProductFromShopify(handle) {
    const response = await fetch(`/products/${handle}.js`);

    if (!response.ok) {
      throw new Error(`Failed to fetch product: ${handle}`);
    }

    return response.json();
  }

  /**
   * Cache helpers
   */
  hasCache(handle) {
    return this.cache.has(handle);
  }

  getCache(handle) {
    return this.cache.get(handle);
  }

  setCache(handle, data) {
    this.cache.set(handle, data);
  }

  /**
   * Extract color from product title
   */
  extractColorFromTitle(title) {
    if (title && title.includes(' - ')) {
      return title.split(' - ')[1];
    }
    return title || '';
  }

  /**
   * Parse JSON safely
   */
  parseJSON(str) {
    try {
      return JSON.parse(str || '[]');
    } catch (error) {
      return [];
    }
  }

  /**
   * Open drawer with loading skeleton
   */
  openWithLoading() {
    this.isLoading = true;

    this.titleEl.textContent = this.triggerData.title;

    this.skeleton.style.display = 'block';
    this.content.style.display = 'none';
    this.errorContainer.style.display = 'none';

    this.drawer.setAttribute('aria-hidden', 'false');
    this.drawer.classList.add('quick-add-drawer--visible');
    document.documentElement.classList.add('modal-js--open');
    this.isOpen = true;

    setTimeout(() => {
      this.focusTrap.activate();
    }, 500);
  }

  /**
   * Show content (after data loaded)
   */
  showContent() {
    this.isLoading = false;

    this.skeleton.style.display = 'none';
    this.content.style.display = 'block';

    this.renderColors();
    this.renderSizes();
    this.updateButtonState();
  }

  /**
   * Show error state
   */
  showError(error) {
    window.console.error('Quick add error:', error);
    this.isLoading = false;

    this.skeleton.style.display = 'none';
    this.content.style.display = 'none';
    this.errorContainer.style.display = 'block';
  }

  /**
   * Retry loading after error
   */
  async retryLoad() {
    this.errorContainer.style.display = 'none';
    this.skeleton.style.display = 'block';
    this.isLoading = true;

    try {
      await this.loadProductData();
      this.showContent();
    } catch (error) {
      this.showError(error);
    }
  }

  close() {
    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawer.classList.remove('quick-add-drawer--visible');
    this.focusTrap.deactivate();
    this.isOpen = false;
    this.isLoading = false;

    if (!document.querySelector('.modal--visible')) {
      document.documentElement.classList.remove('modal-js--open');
    }

    this.triggerData = null;
    this.productData = null;
    this.selectedColor = null;
    this.selectedSize = null;
    this.selectedVariantId = null;
  }

  renderColors() {
    if (!this.productData || this.productData.length === 0) {
      this.colorsContainer.style.display = 'none';
      return;
    }

    if (this.productData.length === 1) {
      this.colorsContainer.style.display = 'none';
      this.selectedColor = this.productData[0].color;
      return;
    }

    this.colorsContainer.style.display = 'block';

    const html = `
      <label class="quick-add-drawer__colors-label body-sm-short">
        ${window.theme.strings?.selectColor || 'Select Color'}
      </label>
      <div class="quick-add-drawer__colors-list">
        ${this.productData
          .map((product) => {
            const isActive = product.color === this.selectedColor;
            return `
            <button
              type="button"
              class="quick-add-drawer__color-swatch ${isActive ? 'quick-add-drawer__color-swatch--active' : ''}"
              data-color="${this.escapeHtml(product.color)}"
              data-handle="${product.handle}"
              aria-pressed="${isActive}"
            >
              <span class="quick-add-drawer__color-image">
                <img
                  src="${product.flatlay}"
                  alt="${this.escapeHtml(product.color)}"
                  width="60"
                  height="75"
                  loading="lazy"
                />
              </span>
              <span class="quick-add-drawer__color-label body-sm-short">${this.escapeHtml(product.color)}</span>
            </button>
          `;
          })
          .join('')}
      </div>
    `;

    this.colorsContainer.innerHTML = html;

    this.colorsContainer.querySelectorAll('[data-color]').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        const color = event.currentTarget.dataset.color;
        this.selectColor(color);
      });
    });
  }

  selectColor(color) {
    const previousSize = this.selectedSize;
    this.selectedColor = color;

    this.renderColors();

    this.renderSizes();

    const newColorProduct = this.productData.find((product) => product.color === color);
    if (newColorProduct && previousSize) {
      const sizeStillAvailable = newColorProduct.variants.find(
        (variant) =>
          variant.option1 === previousSize || variant.option2 === previousSize || variant.option3 === previousSize
      );
      if (sizeStillAvailable) {
        this.selectSize(sizeStillAvailable.id, previousSize);
      }
    }

    this.updateButtonState();
  }

  renderSizes() {
    const selectedProduct = this.productData.find((product) => product.color === this.selectedColor);

    if (!selectedProduct) {
      return;
    }

    const {variants} = selectedProduct;

    if (variants.length === 1) {
      this.sizesContainer.style.display = 'none';
      this.selectedSize = variants[0].option1 || variants[0].option2 || variants[0].option3 || 'ONE SIZE';
      this.selectedVariantId = variants[0].id;
      this.updateButtonState();
      return;
    }

    this.sizesContainer.style.display = 'block';

    const html = variants
      .map((variant) => {
        const size = variant.option1 || variant.option2 || variant.option3;
        const isActive = size === this.selectedSize;
        return `
        <input
          type="radio"
          name="quick-add-size"
          id="quick-add-size-${variant.id}"
          value="${variant.id}"
          data-size="${this.escapeHtml(size)}"
          class="visually-hidden"
          ${variant.available ? '' : 'disabled'}
          ${isActive ? 'checked' : ''}
        />
        <label
          for="quick-add-size-${variant.id}"
          class="body-sm-short"
        >
          <span>${this.escapeHtml(size)}</span>
        </label>
      `;
      })
      .join('');

    this.sizesFieldset.innerHTML = html;

    this.sizesFieldset.querySelectorAll('input').forEach((input) => {
      input.addEventListener('change', (event) => {
        const variantId = parseInt(event.target.value, 10);
        const size = event.target.dataset.size;
        this.selectSize(variantId, size);
      });
    });
  }

  selectSize(variantId, size) {
    this.selectedSize = size;
    this.selectedVariantId = variantId;
    this.updateButtonState();
  }

  updateButtonState() {
    const isEnabled = this.selectedColor && this.selectedVariantId;
    this.addButton.disabled = !isEnabled;

    if (isEnabled) {
      this.addButton.textContent = window.theme.strings?.addToCart || 'Add To Bag';
    } else {
      this.addButton.textContent = window.theme.strings?.selectSize || 'Select A Size';
    }
  }

  addToCart() {
    if (!this.selectedVariantId) return;

    const selectedProduct = this.productData.find((product) => product.color === this.selectedColor);
    const selectedVariant = selectedProduct.variants.find((variant) => variant.id === this.selectedVariantId);

    this.addButton.disabled = true;

    window.sideCart.handleAddToCartEvent(
      {
        id: this.selectedVariantId,
        quantity: 1,
        ...(selectedVariant.compare_at_price &&
          selectedVariant.compare_at_price > selectedVariant.price && {
            properties: {
              _compare_at_price: selectedVariant.compare_at_price,
            },
          }),
      },
      false,
      () => {
        this.showSuccessState(this.selectedSize);
      }
    );
  }

  showSuccessState(size) {
    this.addButton.classList.add('quick-add-drawer__add-to-bag--success');
    this.addButton.innerHTML = `
      <svg width="13" height="13" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-right: 8px;">
        <path d="M4.1001 7.76672L6.43343 9.76672L10.4334 4.76672M13.9334 7.26664C13.9334 10.9485 10.9487 13.9333 7.26676 13.9333C3.58487 13.9333 0.600098 10.9485 0.600098 7.26664C0.600098 3.58474 3.58487 0.599976 7.26676 0.599976C10.9487 0.599976 13.9334 3.58474 13.9334 7.26664Z" stroke="currentColor" stroke-width="1.2"/>
      </svg>
      Added ${this.escapeHtml(size)} To Bag
    `;

    setTimeout(() => {
      window.console.log('🚪 Closing quick add drawer');
      this.close();
      setTimeout(() => {
        this.addButton.classList.remove('quick-add-drawer__add-to-bag--success');
        this.addButton.textContent = window.theme.strings?.addToCart || 'Add To Bag';
        this.addButton.disabled = false;

        window.console.log('🛒 Opening side cart');
        const cartModal = document.querySelector('[data-modal-cart]');

        if (document.body.classList.contains('side-cart-is-visible')) {
          window.console.log('ℹ️ Side cart already open');
        } else {
          document.body.classList.add('side-cart-is-visible');
          if (cartModal) {
            cartModal.setAttribute('aria-hidden', false);
          }
          window.console.log('✅ Side cart opened');
        }
      }, 200);
    }, 400);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

function initQuickAddDrawer() {
  // Always initialize the drawer - it will handle viewport detection internally
  const drawer = new QuickAddDrawer();
  return drawer;
}

export default initQuickAddDrawer;
